const { getOffsetTop } = window.BAO.Utils

class FeatureScroller extends window.BAO.CustomElement() {
  connectedCallback () {
    this.updateEl = this.updateEl.bind(this)

    super.connectedCallback()

    this.isTicking = false
    this.lastKnownY = window.scrollY
    this.activeIndex = -1

    this.trackEl = this.els.track?.element
    this.pinEl = this.els.pin?.element
    this.itemEls = this.els.item?.elements ?? []
    this.mediaEls = this.els.media?.elements ?? []

    this.currentMode = this.mode

    this.updateEl()
  }

  setupListeners () {
    this.onScroll = this.onScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleTrigger = this.handleTrigger.bind(this)

    this.listeners.add(window, 'scroll', this.onScroll, { passive: true })
    this.listeners.add(window, 'resize', this.handleResize)
    this.listeners.add(this, 'click', this.handleTrigger)
  }

  onScroll () {
    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    if (!this.stepCount || !this.trackEl || !this.pinEl) return
    if (this.mode !== 'pinned') return

    this.setActiveIndex(this.pinnedIndex)
  }

  handleResize () {
    if (this.mode === this.currentMode) return

    this.currentMode = this.mode

    this.updateEl()
  }

  /**
   * While pinned the items don't move, so the active index comes from how far
   * we've travelled through the track behind them.
   *
   * @returns {number}
   */
  get pinnedIndex () {
    const distance = this.trackEl.offsetHeight - this.pinEl.offsetHeight

    if (distance <= 0) return 0

    const progress =
      (this.lastKnownY - getOffsetTop(this.trackEl)) / distance

    return this.clampIndex(Math.floor(progress * this.stepCount))
  }


  /**
   * @param {number} index
   *
   * @returns {number}
   */
  clampIndex (index) {
    return Math.min(Math.max(index, 0), this.stepCount - 1)
  }

  /**
   * @param {number} activeIndex
   *
   * @returns {void}
   */
  setActiveIndex (activeIndex) {
    if (activeIndex === this.activeIndex) return

    this.activeIndex = activeIndex

    this.itemEls.forEach((itemEl, index) => {
      itemEl.setAttribute('aria-current', index === activeIndex)
    })

    this.mediaEls.forEach((mediaEl, index) => {
      mediaEl.setAttribute('aria-hidden', index !== activeIndex)
    })
  }

  handleTrigger (event) {
    const { trigger, item } = this.els
    if (!trigger || !item) return

    const triggerEl = event.target.closest(trigger.selector)
    if (!triggerEl) return

    const index = this.itemEls.indexOf(triggerEl.closest(item.selector))
    if (index === -1) return

    if (this.mode !== 'pinned') {
      this.setActiveIndex(index)

      return
    }

    const distance = this.trackEl.offsetHeight - this.pinEl.offsetHeight
    const stepSize = distance / this.stepCount

    window.scrollTo({
      top: getOffsetTop(this.trackEl) + stepSize * index + stepSize / 2,
      behavior: 'smooth',
    })
  }

  /**
   * @returns {number}
   */
  get stepCount () {
    return this.itemEls.length
  }

  /**
   * Pinned drives the active item from scroll position. Below the breakpoint
   * nothing is pinned or sticky, so the item taps drive it instead.
   *
   * @returns {string}
   */
  get mode () {
    if (!window.BAO.MediaBreakpoints.isMd) return 'static'

    return this.classList.contains('fea-Features-noEffectDesktop') ? 'static' : 'pinned'
  }
}

if (!customElements.get('feature-scroller')) {
  customElements.define('feature-scroller', FeatureScroller)
}
